import { getDatabase, ref, get } from 'firebase/database';

/**
 * Calculate the next available sort order for a new favorite
 * This ensures unique, sequential sort orders
 */
export const calculateNextSortOrder = async (userId: string): Promise<number> => {
  try {
    const db = getDatabase();
    const userFavoritesRef = ref(db, `user/${userId}/favorites`);
    const snapshot = await get(userFavoritesRef);
    
    if (!snapshot.exists()) {
      return 1; // First favorite
    }
    
    const favoritesData = snapshot.val();
    let maxSortOrder = 0;
    
    // Find the highest sort order currently in use
    for (const itemName in favoritesData) {
      const itemData = favoritesData[itemName];
      if (itemData.FavoritedSortOrder && itemData.FavoritedSortOrder > maxSortOrder) {
        maxSortOrder = itemData.FavoritedSortOrder;
      }
    }
    
    return maxSortOrder + 1;
  } catch (error) {
    console.error('Error calculating next sort order:', error);
    // Fallback: use timestamp as sort order
    return Date.now();
  }
};

/**
 * Normalize sort orders to ensure they are sequential (1, 2, 3, 4, ...)
 * This fixes any gaps or duplicates in existing sort orders
 */
export const normalizeSortOrders = async (userId: string): Promise<void> => {
  try {
    const db = getDatabase();
    const userFavoritesRef = ref(db, `user/${userId}/favorites`);
    const snapshot = await get(userFavoritesRef);
    
    if (!snapshot.exists()) {
      return;
    }
    
    const favoritesData = snapshot.val();
    const favorites: Array<{ name: string; sortOrder: number; data: any }> = [];
    
    // Collect all favorites with their current sort orders
    for (const itemName in favoritesData) {
      const itemData = favoritesData[itemName];
      if (itemData.FavoritedSortOrder) {
        favorites.push({
          name: itemName,
          sortOrder: itemData.FavoritedSortOrder,
          data: itemData
        });
      }
    }
    
    // Sort by current sort order
    favorites.sort((a, b) => a.sortOrder - b.sortOrder);
    
    // Check if normalization is needed
    let needsNormalization = false;
    for (let i = 0; i < favorites.length; i++) {
      if (favorites[i].sortOrder !== i + 1) {
        needsNormalization = true;
        break;
      }
    }
    
    if (needsNormalization) {
      console.log('🔄 Normalizing sort orders for user:', userId);
      
      // Update sort orders to be sequential
      const updates: { [key: string]: any } = {};
      favorites.forEach((favorite, index) => {
        const newSortOrder = index + 1;
        if (favorite.sortOrder !== newSortOrder) {
          updates[`user/${userId}/favorites/${favorite.name}/FavoritedSortOrder`] = newSortOrder;
          console.log(`📊 Normalizing ${favorite.name}: ${favorite.sortOrder} → ${newSortOrder}`);
        }
      });
      
      if (Object.keys(updates).length > 0) {
        const { update } = await import('firebase/database');
        await update(ref(db), updates);
        console.log('✅ Sort orders normalized');
      }
    }
  } catch (error) {
    console.error('Error normalizing sort orders:', error);
  }
};
